import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface InactivatePriceTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId?: string;
  onSuccess?: () => void;
}

export function InactivatePriceTableDialog({
  open,
  onOpenChange,
  priceTableId,
  onSuccess,
}: InactivatePriceTableDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleInactivate = async () => {
    if (!priceTableId) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'ID da tabela não encontrado.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await priceTableApi.inactivate(priceTableId);

      if (result.success) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: 'Tabela inativada com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
        navigate('/commercial/price-table');
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: result.error?.global || 'Erro ao inativar tabela. Tente novamente.',
        });
      }
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao inativar tabela. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-4 rounded-lg p-5 [&>button]:hidden">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
            <TriangleAlert className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
          <DialogHeader className="space-y-2 pb-0 text-left">
            <DialogTitle className="text-base leading-5">Inativar tabela!</DialogTitle>
            <DialogDescription className="text-xs leading-[18px] text-gray-500">
              Ao confirmar esta ação, a tabela será inativada. No caso de tabela do tipo normal será
              necessário aguardar o cadastro e publicação/ativação de nova tabela para que os preços
              voltem a aparecer nas propostas caso não tenha uma tabela provisória em vigor. No caso
              de tabela do tipo provisória, automaticamente a tabela do tipo normal que estiver 'Em
              Pausa' será reativada se houver. Caso não tenha tabela 'Em Pausa' então será
              necessário aguardar o cadastro e publicação/ativação de nova tabela para que os preços
              voltem a aparecer na proposta.
            </DialogDescription>
          </DialogHeader>
        </div>
        <DialogFooter className="flex-row justify-end gap-2 pt-0 sm:space-x-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button size="sm" variant="destructive" onClick={handleInactivate} disabled={isLoading}>
            {isLoading ? 'Inativando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
